/**
 * WorkspaceDistributionConfig - Configuração de distribuição automática para Workspaces
 * 
 * Componente de conteúdo para habilitar/desabilitar distribuição automática 
 * e selecionar uma fila de distribuição global para um workspace CRM.
 * 
 * @version 1.1.0
 * @date 2026-01-20
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

// Ícones
import { Layers, Check, AlertCircle, AlertTriangle } from 'lucide-react';

// Componentes UI
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { DialogFooter } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';

// Logger
const logger = createLogger('WorkspaceDistributionConfig');

// ==============================================================
// Interfaces
// ==============================================================

interface WorkspaceDistributionConfigProps {
  /** ID do workspace */
  workspaceId: string;
  /** Nome do workspace (para exibição) */
  workspaceName: string;
  /** Estado atual de distribuição automática */
  autoDistributionEnabled?: boolean;
  /** ID da fila atual (se houver) */
  currentQueueId?: string | null;
  /** Callback após salvar */
  onSave?: () => void;
}

interface DistributionQueue {
  id: string;
  name: string;
  context_type: 'chat' | 'crm' | 'all';
  distribution_method: string;
  is_active: boolean;
}

/** Resultado da validação de sellers dos membros da fila */
interface QueueSellerValidation {
  totalActive: number;
  withSeller: number;
  withoutSeller: number;
}

// ==============================================================
// Componente Principal
// ==============================================================

export function WorkspaceDistributionConfig({
  workspaceId,
  workspaceName,
  autoDistributionEnabled = false,
  currentQueueId = null,
  onSave,
}: WorkspaceDistributionConfigProps) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  // Estados locais
  const [enabled, setEnabled] = useState(autoDistributionEnabled);
  const [selectedQueueId, setSelectedQueueId] = useState<string | null>(currentQueueId);

  // Atualiza estados quando props mudam
  useEffect(() => {
    setEnabled(autoDistributionEnabled);
    setSelectedQueueId(currentQueueId);
  }, [autoDistributionEnabled, currentQueueId]);

  // ==============================================================
  // Queries
  // ==============================================================

  /** Busca filas de distribuição disponíveis para CRM */
  const { data: queues, isLoading: isQueuesLoading } = useQuery({
    queryKey: ['distribution-queues-crm', tenantId],
    queryFn: async (): Promise<DistributionQueue[]> => {
      if (!tenantId) return [];

      const { data, error } = await supabase
        .from('distribution_queues')
        .select('id, name, context_type, distribution_method, is_active')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .in('context_type', ['crm', 'all'])
        .order('name');

      if (error) {
        logger.error('Erro ao buscar filas', error);
        return [];
      }

      return data || [];
    },
    enabled: !!tenantId,
  });

  /** 
   * Valida quantos membros da fila selecionada possuem vendedor vinculado
   * Usado para alertar usuários sobre possíveis problemas de atribuição
   */
  const { data: queueValidation, isLoading: isValidating } = useQuery({
    queryKey: ['queue-seller-validation', selectedQueueId, tenantId],
    queryFn: async (): Promise<QueueSellerValidation> => {
      if (!selectedQueueId || !tenantId) {
        return { totalActive: 0, withSeller: 0, withoutSeller: 0 };
      }

      // Busca membros ativos da fila
      const { data: members, error: membersError } = await supabase
        .from('distribution_queue_members')
        .select('user_id, is_active')
        .eq('queue_id', selectedQueueId)
        .eq('is_active', true);

      if (membersError || !members?.length) {
        logger.warn('Fila sem membros ativos', { queueId: selectedQueueId });
        return { totalActive: 0, withSeller: 0, withoutSeller: 0 };
      }

      const memberUserIds = members.map(m => m.user_id);

      // Busca sellers vinculados aos user_ids dos membros
      const { data: sellers } = await supabase
        .from('crm_sellers')
        .select('user_id')
        .in('user_id', memberUserIds)
        .eq('tenant_id', tenantId)
        .eq('is_active', true);

      const sellerUserIds = new Set(sellers?.map(s => s.user_id) || []);
      const withSeller = members.filter(m => sellerUserIds.has(m.user_id)).length;

      return {
        totalActive: members.length,
        withSeller,
        withoutSeller: members.length - withSeller,
      };
    },
    enabled: !!selectedQueueId && !!tenantId && enabled,
  });

  // ==============================================================
  // Mutations
  // ==============================================================

  /** Salva configuração de distribuição do workspace */
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!tenantId) throw new Error('Tenant não encontrado');

      logger.info('Salvando configuração de distribuição', {
        workspaceId,
        enabled,
        queueId: selectedQueueId,
      });

      // Atualiza direto na tabela crm_workspaces
      const { error } = await supabase
        .from('crm_workspaces')
        .update({
          auto_distribution_enabled: enabled,
          default_queue_id: enabled ? selectedQueueId : null,
        })
        .eq('id', workspaceId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-workspaces-stats'] });
      toast.success('Configuração salva com sucesso!');
      onSave?.();
    },
    onError: (error: any) => {
      logger.error('Erro ao salvar configuração', error);
      toast.error(error.message || 'Erro ao salvar configuração');
    },
  });

  // ==============================================================
  // Render
  // ==============================================================

  return (
    <div className="space-y-6 py-4">
      {/* Toggle de ativação */}
      <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
        <div className="space-y-0.5">
          <Label className="text-base font-medium">Ativar Distribuição</Label>
          <p className="text-sm text-muted-foreground">
            Novos negócios serão atribuídos automaticamente
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={setEnabled}
        />
      </div>

      {/* Seleção de fila */}
      {enabled && (
        <div className="space-y-3">
          <Label>Fila de Distribuição</Label>
          
          {isQueuesLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : queues && queues.length > 0 ? (
            <Select
              value={selectedQueueId || ''}
              onValueChange={(value) => setSelectedQueueId(value || null)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma fila..." />
              </SelectTrigger>
              <SelectContent>
                {queues.map((queue) => (
                  <SelectItem key={queue.id} value={queue.id}>
                    <div className="flex items-center gap-2">
                      <Layers className="h-4 w-4 text-muted-foreground" />
                      <span>{queue.name}</span>
                      <Badge variant="outline" className="text-[10px] px-1.5">
                        {queue.distribution_method === 'round_robin' ? 'RR' : 'LB'}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="flex items-center gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-700">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <p className="text-sm">
                Nenhuma fila CRM disponível. Crie uma fila em Distribuição &gt; Filas.
              </p>
            </div>
          )}

          {/* Validação de sellers dos membros */}
          {selectedQueueId && !isValidating && queueValidation && (
            <>
              {/* Erro: Nenhum membro com seller */}
              {queueValidation.withSeller === 0 && queueValidation.totalActive > 0 && (
                <div className="p-3 rounded-lg border border-destructive/30 bg-destructive/10">
                  <div className="flex items-center gap-2 text-destructive">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span className="font-medium text-sm">Fila sem vendedores vinculados</span>
                  </div>
                  <p className="text-xs text-destructive/80 mt-1">
                    Nenhum dos {queueValidation.totalActive} membros possui vendedor cadastrado.
                    Acesse CRM → Vendedores para vincular.
                  </p>
                </div>
              )}

              {/* Aviso: Alguns membros sem seller */}
              {queueValidation.withSeller > 0 && queueValidation.withoutSeller > 0 && (
                <div className="p-3 rounded-lg border border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20">
                  <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span className="font-medium text-sm">Atenção: membros sem vendedor</span>
                  </div>
                  <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-1">
                    {queueValidation.withoutSeller} de {queueValidation.totalActive} membros 
                    não possuem vendedor vinculado.
                  </p>
                </div>
              )}

              {/* Sucesso: Todos com seller */}
              {queueValidation.withSeller > 0 && queueValidation.withoutSeller === 0 && (
                <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                  <Check className="h-3 w-3" />
                  <span>{queueValidation.withSeller} membro(s) com vendedor vinculado</span>
                </div>
              )}

              {/* Info padrão */}
              {queueValidation.withSeller > 0 && (
                <p className="text-xs text-muted-foreground">
                  Os negócios criados neste workspace serão automaticamente distribuídos para os
                  membros da fila selecionada.
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* Aviso quando desativado */}
      {!enabled && (
        <div className="p-3 rounded-lg border bg-muted/50 text-sm text-muted-foreground">
          Com a distribuição desativada, os negócios serão criados sem atribuição automática.
        </div>
      )}

      <DialogFooter>
        <Button
          onClick={() => saveMutation.mutate()}
          disabled={
            saveMutation.isPending || 
            isValidating ||
            (enabled && !selectedQueueId) ||
            (enabled && queueValidation?.withSeller === 0 && (queueValidation?.totalActive ?? 0) > 0)
          }
          className="gap-2"
        >
          {saveMutation.isPending ? (
            'Salvando...'
          ) : (
            <>
              <Check className="h-4 w-4" />
              Salvar
            </>
          )}
        </Button>
      </DialogFooter>
    </div>
  );
}
